import React from 'react';
import './PopularPages.css';

interface PopularPagesProps {
  mostViewed: Array<{ page_number: number; view_count: number }>;
  dropOffPages: number[];
}

const PopularPages: React.FC<PopularPagesProps> = ({ mostViewed, dropOffPages }) => {
  // Sort most viewed pages by view count
  const sortedMostViewed = [...mostViewed].sort((a, b) => b.view_count - a.view_count);
  
  // Get least viewed pages (bottom 3)
  const leastViewed = [...mostViewed]
    .sort((a, b) => a.view_count - b.view_count)
    .slice(0, 3);

  const getPageRank = (index: number) => {
    const ranks = ['🥇', '🥈', '🥉'];
    return ranks[index] || `#${index + 1}`;
  };

  const getEngagementLevel = (viewCount: number, maxViews: number) => {
    const percentage = maxViews > 0 ? (viewCount / maxViews) * 100 : 0;
    
    if (percentage >= 80) return { level: 'Excellent', color: '#4caf50', icon: '🔥' };
    if (percentage >= 60) return { level: 'Good', color: '#2c5aa0', icon: '👍' };
    if (percentage >= 40) return { level: 'Average', color: '#ff9800', icon: '📊' };
    if (percentage >= 20) return { level: 'Below Average', color: '#f44336', icon: '📉' };
    return { level: 'Poor', color: '#9e9e9e', icon: '💤' };
  };

  const maxViews = Math.max(...mostViewed.map(p => p.view_count));

  return (
    <div className="popular-pages">
      <h3>📊 Page Performance</h3>
      
      <div className="pages-grid">
        {/* Most Viewed Pages */}
        <div className="pages-section">
          <h4>🏆 Most Viewed Pages</h4>
          <div className="pages-list">
            {sortedMostViewed.slice(0, 5).map((page, index) => {
              const engagement = getEngagementLevel(page.view_count, maxViews);
              return (
                <div key={page.page_number} className="page-item most-viewed">
                  <div className="page-rank">
                    {getPageRank(index)}
                  </div>
                  <div className="page-info">
                    <div className="page-number">Page {page.page_number}</div>
                    <div className="page-stats">
                      <span className="view-count">{page.view_count} views</span>
                      <span 
                        className="engagement-level"
                        style={{ color: engagement.color }}
                      >
                        {engagement.icon} {engagement.level}
                      </span>
                    </div>
                  </div>
                  <div className="page-bar">
                    <div 
                      className="page-fill"
                      style={{ 
                        width: `${(page.view_count / maxViews) * 100}%`,
                        backgroundColor: engagement.color
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Least Viewed Pages */}
        <div className="pages-section">
          <h4>📉 Least Viewed Pages</h4>
          <div className="pages-list">
            {leastViewed.map((page, index) => {
              const engagement = getEngagementLevel(page.view_count, maxViews);
              return (
                <div key={page.page_number} className="page-item least-viewed">
                  <div className="page-rank warning">
                    ⚠️
                  </div>
                  <div className="page-info">
                    <div className="page-number">Page {page.page_number}</div>
                    <div className="page-stats">
                      <span className="view-count">{page.view_count} views</span>
                      <span 
                        className="engagement-level"
                        style={{ color: engagement.color }}
                      >
                        {engagement.icon} {engagement.level}
                      </span>
                    </div>
                  </div>
                  <div className="page-bar">
                    <div 
                      className="page-fill"
                      style={{ 
                        width: `${(page.view_count / maxViews) * 100}%`,
                        backgroundColor: engagement.color
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Drop-off Pages */}
      {dropOffPages.length > 0 && (
        <div className="dropoff-section">
          <h4>🚪 High Drop-off Pages</h4>
          <div className="dropoff-pages">
            {dropOffPages.map((pageNumber) => (
              <div key={pageNumber} className="dropoff-page">
                <span className="dropoff-icon">🚪</span>
                <span className="dropoff-text">Page {pageNumber}</span>
                <span className="dropoff-warning">Users often leave here</span>
              </div>
            ))}
          </div>
          <div className="dropoff-advice">
            <div className="advice-icon">💡</div>
            <div className="advice-text">
              Consider reviewing content on these pages - they may need improvement 
              to maintain reader engagement.
            </div>
          </div>
        </div>
      )}

      {/* Performance Insights */}
      <div className="performance-insights">
        <h4>🎯 Performance Insights</h4>
        <div className="insights-list">
          {sortedMostViewed.length > 0 && (
            <div className="insight-item success">
              <span className="insight-icon">🏆</span>
              <span className="insight-text">
                Page {sortedMostViewed[0].page_number} is your star performer with {sortedMostViewed[0].view_count} views
              </span>
            </div>
          )}
          
          {leastViewed.length > 0 && leastViewed[0].view_count < maxViews * 0.3 && (
            <div className="insight-item warning">
              <span className="insight-icon">⚠️</span>
              <span className="insight-text">
                Page {leastViewed[0].page_number} has significantly lower engagement - consider content review
              </span>
            </div>
          )}
          
          {dropOffPages.length > 0 && (
            <div className="insight-item info">
              <span className="insight-icon">📊</span>
              <span className="insight-text">
                {dropOffPages.length} page{dropOffPages.length !== 1 ? 's' : ''} showing high drop-off rates
              </span>
            </div>
          )}
          
          {sortedMostViewed.length >= 3 && (
            <div className="insight-item neutral">
              <span className="insight-icon">📈</span>
              <span className="insight-text">
                Top 3 pages account for {Math.round(
                  (sortedMostViewed.slice(0, 3).reduce((sum, p) => sum + p.view_count, 0) / 
                   sortedMostViewed.reduce((sum, p) => sum + p.view_count, 0)) * 100
                )}% of total views
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h4>⚡ Quick Actions</h4>
        <div className="actions-grid">
          <button className="action-btn primary">
            <span className="action-icon">🔍</span>
            <span className="action-text">Analyze Top Pages</span>
          </button>
          <button className="action-btn secondary">
            <span className="action-icon">🛠️</span>
            <span className="action-text">Improve Low Performers</span>
          </button>
          <button className="action-btn tertiary">
            <span className="action-icon">📊</span>
            <span className="action-text">Export Page Report</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PopularPages;